import {pick} from "@pg-mono/nodash";
import {IOfferFriendlySubFilterGeneralUrlParams, IOfferFriendlySubTypeUrlParams} from "@pg-mono/rp-routes";
import {safelyParsePage} from "@pg-mono/string-utils";

import {trimAggregationFromRegion} from "../../../meta_data/utils/trim_aggregation_from_region";
import {RegionType} from "../../../region/types/RegionType";
import {ILatestQuery, parseQueryParamToInt} from "../../../utils/latest_query";
import {OfferMetaRobots} from "../../constants/offer_meta_robots";
import {getOfferListUrlStrategy} from "../../helpers/offer_list_url/get_offer_list_url_strategy";
import {hasOfferListFilterInQuery} from "../../helpers/offer_list_url/offer_list_url_filters";
import {IOfferListingSeoRule, IOfferListingSeoRuleParams, offerFilterKeys} from "../../helpers/seo/constants";
import {
    createCanonicalAndRobotsHouseFilterOnlyRule,
    createCanonicalAndRobotsHouseFilterWithOtherFiltersRule
} from "../../helpers/seo/offer_listing_cannical_and_robots_house_filter_rules";
import {
    createCanonicalAndRobotsCommonAreaFilterRule,
    createCanonicalAndRobotsFlatAreaFilterRule,
    createCanonicalAndRobotsHouseAreaFilterRule
} from "../../helpers/seo/offer_listing_canonical_and_robots_area_rules";
import {getOfferListingSeoCanonicalByRules, getOfferListingSeoRobotsByRules} from "../../helpers/seo/offer_listing_canonical_and_robots_helpers";
import {createCanonicalAndRobotsInstalmentFilterRule} from "../../helpers/seo/offer_listing_canonical_and_robots_instalmenr_rules";
import {
    createCanonicalAndRobotsCommonRoomsFilterRule,
    createCanonicalAndRobotsFlatRoomsFilterRule,
    createCanonicalAndRobotsHouseRoomsFilterRule
} from "../../helpers/seo/offer_listing_canonical_and_robots_rooms_rules";
import {
    createCanonicalAndRobotsCommercialAndMixedTypeFilterRule,
    createCanonicalPageRule,
    createGeneralSeoRobotsRule,
    createRobotsMixedTypeForCustomFilterRule,
    createSpecialAbroadListingsRobotsRule,
    createSpecialListingCanonicalRule,
    createSpecialListingsRobotsRule
} from "../../helpers/seo/offer_listing_canonical_and_robots_rules";
import {createMultiFilterRobotsRule} from "../../helpers/seo/offer_listing_multi_filter_robots_rule";
import {
    createRobotsPriceCommercialRule,
    createRobotsPriceFurtherPagesRule,
    createRobotsPriceGeneralRule,
    createRobotsPriceToFilterRule
} from "../../helpers/seo/offer_listing_robots_price_rules";
import {PRIVILEGE_OFFER_QUERY_PARAM_NAME} from "../constants/offer_list";

export interface GetOfferListRobotsCanonicalMetaParams {
    soonFinishedOffersCount: number;
    unavailableOffersCount: number;
    offersCount: number;
    latestQuery: ILatestQuery;
    hasSeoDescription: boolean;
    regions:
        | {
              id: number;
              type: RegionType;
              slug: string;
          }[]
        | undefined;
    ctx: {
        route: {
            pathname: string;
            query: Record<string, string | string[]>;
        };
        match: {
            params: Partial<IOfferFriendlySubFilterGeneralUrlParams & IOfferFriendlySubTypeUrlParams>;
        };
    };
}

/** `canonical` is null when the page must not declare one (a multifilter served as query params). */
export const getOfferListsRobotsCanonicalMeta = (params: GetOfferListRobotsCanonicalMetaParams): {robots: OfferMetaRobots; canonical: string | null} => {
    const {ctx, latestQuery, offersCount, regions} = params;
    const commonSeoParams = {
        offerType: parseQueryParamToInt(latestQuery.type),
        regions,
        filters: pick(latestQuery, offerFilterKeys),
        sort: latestQuery.sort,
        page: safelyParsePage(ctx.route.query.page) || undefined,
        country: parseQueryParamToInt(latestQuery.country)
    };
    const isMultiFilter = !getOfferListUrlStrategy().isIndexable({
        ...latestQuery,
        region: regions?.[0]?.slug
    });
    const commonCanonicalAndRobotsRules = getCanonicalAndRobotsRules(commonSeoParams);
    const offerListSubFilterRobotsAndCanonicalRules = getCanonicalAndRobotsRulesForHouseFilter(
        commonSeoParams,
        offersCount,
        ctx.match.params.offerListSubFilter
    );

    const robots = getOfferListRobots(params, commonSeoParams, [
        createMultiFilterRobotsRule(isMultiFilter),
        ...offerListSubFilterRobotsAndCanonicalRules,
        ...commonCanonicalAndRobotsRules
    ]);

    if (isMultiFilter && hasOfferListFilterInQuery(ctx.route.query)) {
        return {robots, canonical: null};
    }

    const customCanonicalPath = getOfferListingSeoCanonicalByRules([
        ...offerListSubFilterRobotsAndCanonicalRules,
        createCanonicalPageRule(commonSeoParams),
        createSpecialListingCanonicalRule({
            ...commonSeoParams,
            routePathname: ctx.route.pathname
        }),
        ...commonCanonicalAndRobotsRules
    ]);

    return {
        robots,
        canonical: trimAggregationFromRegion(customCanonicalPath || ctx.route.pathname)
    };
};

/** Filter-specific rules decide first; then paging, empty listings, special listings and privileged offers. */
const getOfferListRobots = (
    params: GetOfferListRobotsCanonicalMetaParams,
    seoParams: IOfferListingSeoRuleParams,
    filterRules: IOfferListingSeoRule[]
): OfferMetaRobots => {
    const {ctx, hasSeoDescription, offersCount, unavailableOffersCount, soonFinishedOffersCount} = params;
    const {page, ...restQuery} = ctx.route.query;

    return (
        getOfferListingSeoRobotsByRules([
            ...filterRules,
            createGeneralSeoRobotsRule({
                page: page ? safelyParsePage(page) : undefined,
                seoDescriptionExists: hasSeoDescription,
                sortParamExists: Boolean(restQuery?.sort),
                routeQuery: ctx.route.query,
                soonFinishedOffersCount,
                unavailableOffersCount,
                offersCount
            }),
            createSpecialListingsRobotsRule({...seoParams, routePathname: ctx.route.pathname}),
            createSpecialAbroadListingsRobotsRule({...seoParams, routePathname: ctx.route.pathname}),
            createRobotsMixedTypeForCustomFilterRule({offerType: seoParams.offerType, query: ctx.route.query}),
            {
                condition: () => Boolean(restQuery && restQuery[PRIVILEGE_OFFER_QUERY_PARAM_NAME]),
                robots: OfferMetaRobots.NOINDEX_FOLLOW
            }
        ]) || OfferMetaRobots.INDEX_FOLLOW
    );
};

export const getCanonicalAndRobotsRules = (seoParams: IOfferListingSeoRuleParams) => [
    createRobotsPriceCommercialRule(seoParams),
    createRobotsPriceFurtherPagesRule(seoParams),
    createRobotsPriceToFilterRule(seoParams),
    createCanonicalAndRobotsInstalmentFilterRule(seoParams),
    createCanonicalAndRobotsCommonRoomsFilterRule(seoParams),
    createCanonicalAndRobotsCommonAreaFilterRule(seoParams),
    createCanonicalAndRobotsFlatRoomsFilterRule(seoParams),
    createCanonicalAndRobotsFlatAreaFilterRule(seoParams),
    createCanonicalAndRobotsHouseRoomsFilterRule(seoParams),
    createCanonicalAndRobotsHouseAreaFilterRule(seoParams),
    createCanonicalAndRobotsCommercialAndMixedTypeFilterRule(seoParams),
    createRobotsPriceGeneralRule(seoParams)
];

const getCanonicalAndRobotsRulesForHouseFilter = (seoParams: IOfferListingSeoRuleParams, offerCount: number, offerListSubFilter?: string) => [
    createCanonicalAndRobotsHouseFilterOnlyRule(seoParams, offerCount, offerListSubFilter),
    createCanonicalAndRobotsHouseFilterWithOtherFiltersRule(seoParams, offerCount, offerListSubFilter)
];
import {pick} from "@pg-mono/nodash";
import {IOfferFriendlySubFilterGeneralUrlParams, IOfferFriendlySubTypeUrlParams} from "@pg-mono/rp-routes";
import {safelyParsePage} from "@pg-mono/string-utils";

import {trimAggregationFromRegion} from "../../../meta_data/utils/trim_aggregation_from_region";
import {RegionType} from "../../../region/types/RegionType";
import {ILatestQuery, parseQueryParamToInt} from "../../../utils/latest_query";
import {OfferMetaRobots} from "../../constants/offer_meta_robots";
import {getOfferListUrlStrategy} from "../../helpers/offer_list_url/get_offer_list_url_strategy";
import {hasOfferListFilterInQuery} from "../../helpers/offer_list_url/offer_list_url_filters";
import {IOfferListingSeoRule, IOfferListingSeoRuleParams, offerFilterKeys} from "../../helpers/seo/constants";
import {
    createCanonicalAndRobotsHouseFilterOnlyRule,
    createCanonicalAndRobotsHouseFilterWithOtherFiltersRule
} from "../../helpers/seo/offer_listing_cannical_and_robots_house_filter_rules";
import {
    createCanonicalAndRobotsCommonAreaFilterRule,
    createCanonicalAndRobotsFlatAreaFilterRule,
    createCanonicalAndRobotsHouseAreaFilterRule
} from "../../helpers/seo/offer_listing_canonical_and_robots_area_rules";
import {getOfferListingSeoCanonicalByRules, getOfferListingSeoRobotsByRules} from "../../helpers/seo/offer_listing_canonical_and_robots_helpers";
import {createCanonicalAndRobotsInstalmentFilterRule} from "../../helpers/seo/offer_listing_canonical_and_robots_instalmenr_rules";
import {
    createCanonicalAndRobotsCommonRoomsFilterRule,
    createCanonicalAndRobotsFlatRoomsFilterRule,
    createCanonicalAndRobotsHouseRoomsFilterRule
} from "../../helpers/seo/offer_listing_canonical_and_robots_rooms_rules";
import {
    createCanonicalAndRobotsCommercialAndMixedTypeFilterRule,
    createCanonicalPageRule,
    createGeneralSeoRobotsRule,
    createRobotsMixedTypeForCustomFilterRule,
    createSpecialAbroadListingsRobotsRule,
    createSpecialListingCanonicalRule,
    createSpecialListingsRobotsRule
} from "../../helpers/seo/offer_listing_canonical_and_robots_rules";
import {createMultiFilterRobotsRule} from "../../helpers/seo/offer_listing_multi_filter_robots_rule";
import {
    createRobotsPriceCommercialRule,
    createRobotsPriceFurtherPagesRule,
    createRobotsPriceGeneralRule,
    createRobotsPriceToFilterRule
} from "../../helpers/seo/offer_listing_robots_price_rules";
import {PRIVILEGE_OFFER_QUERY_PARAM_NAME} from "../constants/offer_list";

export interface GetOfferListRobotsCanonicalMetaParams {
    soonFinishedOffersCount: number;
    unavailableOffersCount: number;
    offersCount: number;
    latestQuery: ILatestQuery;
    hasSeoDescription: boolean;
    regions:
        | {
              id: number;
              type: RegionType;
              slug: string;
          }[]
        | undefined;
    ctx: {
        route: {
            pathname: string;
            query: Record<string, string | string[]>;
        };
        match: {
            params: Partial<IOfferFriendlySubFilterGeneralUrlParams & IOfferFriendlySubTypeUrlParams>;
        };
    };
}

/** `canonical` is null when the page must not declare one (a multifilter served as query params). */
export const getOfferListsRobotsCanonicalMeta = (params: GetOfferListRobotsCanonicalMetaParams): {robots: OfferMetaRobots; canonical: string | null} => {
    const {ctx, latestQuery, offersCount, regions} = params;
    const commonSeoParams = {
        offerType: parseQueryParamToInt(latestQuery.type),
        regions,
        filters: pick(latestQuery, offerFilterKeys),
        sort: latestQuery.sort,
        page: safelyParsePage(ctx.route.query.page) || undefined,
        country: parseQueryParamToInt(latestQuery.country)
    };
    const isMultiFilter = !getOfferListUrlStrategy().isIndexable({
        ...latestQuery,
        region: regions?.[0]?.slug
    });
    const commonCanonicalAndRobotsRules = getCanonicalAndRobotsRules(commonSeoParams);
    const offerListSubFilterRobotsAndCanonicalRules = getCanonicalAndRobotsRulesForHouseFilter(
        commonSeoParams,
        offersCount,
        ctx.match.params.offerListSubFilter
    );

    const robots = getOfferListRobots(params, commonSeoParams, [
        createMultiFilterRobotsRule(isMultiFilter),
        ...offerListSubFilterRobotsAndCanonicalRules,
        ...commonCanonicalAndRobotsRules
    ]);

    if (isMultiFilter && hasOfferListFilterInQuery(ctx.route.query)) {
        return {robots, canonical: null};
    }

    const customCanonicalPath = getOfferListingSeoCanonicalByRules([
        ...offerListSubFilterRobotsAndCanonicalRules,
        createCanonicalPageRule(commonSeoParams),
        createSpecialListingCanonicalRule({
            ...commonSeoParams,
            routePathname: ctx.route.pathname
        }),
        ...commonCanonicalAndRobotsRules
    ]);

    return {
        robots,
        canonical: trimAggregationFromRegion(customCanonicalPath || ctx.route.pathname)
    };
};

/** Filter-specific rules decide first; then paging, empty listings, special listings and privileged offers. */
const getOfferListRobots = (
    params: GetOfferListRobotsCanonicalMetaParams,
    seoParams: IOfferListingSeoRuleParams,
    filterRules: IOfferListingSeoRule[]
): OfferMetaRobots => {
    const {ctx, hasSeoDescription, offersCount, unavailableOffersCount, soonFinishedOffersCount} = params;
    const {page, ...restQuery} = ctx.route.query;

    return (
        getOfferListingSeoRobotsByRules([
            ...filterRules,
            createGeneralSeoRobotsRule({
                page: page ? safelyParsePage(page) : undefined,
                seoDescriptionExists: hasSeoDescription,
                sortParamExists: Boolean(restQuery?.sort),
                routeQuery: ctx.route.query,
                soonFinishedOffersCount,
                unavailableOffersCount,
                offersCount
            }),
            createSpecialListingsRobotsRule({...seoParams, routePathname: ctx.route.pathname}),
            createSpecialAbroadListingsRobotsRule({...seoParams, routePathname: ctx.route.pathname}),
            createRobotsMixedTypeForCustomFilterRule({offerType: seoParams.offerType, query: ctx.route.query}),
            {
                condition: () => Boolean(restQuery && restQuery[PRIVILEGE_OFFER_QUERY_PARAM_NAME]),
                robots: OfferMetaRobots.NOINDEX_FOLLOW
            }
        ]) || OfferMetaRobots.INDEX_FOLLOW
    );
};

export const getCanonicalAndRobotsRules = (seoParams: IOfferListingSeoRuleParams) => [
    createRobotsPriceCommercialRule(seoParams),
    createRobotsPriceFurtherPagesRule(seoParams),
    createRobotsPriceToFilterRule(seoParams),
    createCanonicalAndRobotsInstalmentFilterRule(seoParams),
    createCanonicalAndRobotsCommonRoomsFilterRule(seoParams),
    createCanonicalAndRobotsCommonAreaFilterRule(seoParams),
    createCanonicalAndRobotsFlatRoomsFilterRule(seoParams),
    createCanonicalAndRobotsFlatAreaFilterRule(seoParams),
    createCanonicalAndRobotsHouseRoomsFilterRule(seoParams),
    createCanonicalAndRobotsHouseAreaFilterRule(seoParams),
    createCanonicalAndRobotsCommercialAndMixedTypeFilterRule(seoParams),
    createRobotsPriceGeneralRule(seoParams)
];

const getCanonicalAndRobotsRulesForHouseFilter = (seoParams: IOfferListingSeoRuleParams, offerCount: number, offerListSubFilter?: string) => [
    createCanonicalAndRobotsHouseFilterOnlyRule(seoParams, offerCount, offerListSubFilter),
    createCanonicalAndRobotsHouseFilterWithOtherFiltersRule(seoParams, offerCount, offerListSubFilter)
];
